// serializeProjectToBundle — the PURE inverse of
// `features/page-editor/services/template-loader.ts`'s
// `buildLoadedTemplateFromFiles` (the FORWARD map: bundle `files` ->
// `LoadedApp`). This function walks the studio project-state shapes that
// forward map produces (manifest / design-system source / per-block
// ui|state|events|flow|data|meta / app-level static-datasource files) and
// reassembles the bundle-relative `files` map `@cogo/template`'s `AppBundle`
// uses: 'app.json', 'design-system.json',
// 'blocks/<id>/{ui,meta,state,events,flow,data}.json', 'data/<name>.json'.
//
// Plan B3 Task 1 (cogo.template.v1 authoring lane). PURE leaf module: no
// store imports, no network, no Date.now/Math.random. Every level of
// iteration (block ids, appDataFiles keys) is walked in SORTED order so two
// calls with the same logical input always produce the same files map (key
// order is not semantically load-bearing for JSON equality, but determinism
// is still part of this module's contract per the task brief).
//
// Faithfulness is PROVEN, not asserted — see
// `__tests__/serialize-project-to-bundle.test.ts`'s round-trip gate, which
// feeds a REAL registry source bundle (apps/cogo-starter/public/apps/
// bakery-shop-new3) through the forward map (`buildLoadedTemplateFromFiles`)
// and back through this function, then diffs the result against the source
// with `@cogo/template`'s `diffDeclaredOnly` gate (the same gate that
// protects the registered template package's own fidelity).
//
// Emission rules (mirrors the forward map's read set exactly):
//
//  - `app.json` <- `input.manifest`, always emitted (the forward map
//    requires app.json to exist at all — there is no "absent manifest"
//    case to invert).
//
//  - `design-system.json` <- TWO-SOURCE rule (B3 Task-2 review fix,
//    2026-07-25): `input.tokens` when non-null/non-undefined (unchanged,
//    byte-faithful — see below); ELSE `input.derivedTokens` when non-null/
//    non-undefined; else omitted entirely. NOTE the naming: despite the
//    field being called `tokens` (matching the task brief's literal
//    interface), it must be fed `LoadedApp.designSystem` (the RAW
//    design-system.json content, `files['design-system.json']` verbatim) —
//    NOT `LoadedApp.tokens` (the forward map's DERIVED `AxesThemeTokens`,
//    produced by `tryDeriveThemeTokensFromDesignSystem`, which for the
//    common "legacy direct-honor" shape only extracts `colors`/`radius`/
//    `fonts` and drops every other design-system.json field — see that
//    function's JSDoc in `@cogo/design-presets`). The brief's own doc
//    comment on this parameter ("design-system.json source") is the tell:
//    this function's `tokens` param holds the FILE's content, not the
//    runtime-derived theme.
//
//    `input.derivedTokens` (new, optional) exists because that byte-
//    faithful raw file is a LUXURY that only bundle-loaded projects
//    (template instantiation, local import) ever had — `LoadedApp.
//    designSystem` is populated ONLY by `buildLoadedTemplateFromFiles`
//    reading an on-disk `design-system.json`. A studio project opened via
//    `openServerProject` (`toLoadedApp` in `project-api.ts`) NEVER sets
//    `designSystem` at all; the server's own persisted design truth for
//    that project lives in the `studio_projects.tokens` DB column, surfaced
//    as `LoadedApp.tokens` (`DesignTokens`). Emitting `design-system.json`
//    from `derivedTokens` in that case is NOT the same lossy-projection
//    concern the paragraph above warns about for a bundle that ALSO has a
//    raw file sitting right next to the derived one — for a server project
//    there never WAS a raw design-system.json to be unfaithful to. Deriving
//    IS the source of truth there, not a fallback approximation of one.
//    Priority is still raw-first (`tokens`) when a bundle-loaded project
//    happens to carry both, since that path's byte-faithfulness is the
//    stronger guarantee whenever it's available.
//
//  - `blocks/<id>/{ui,meta,state,events,data}.json` <- the matching key on
//    `input.blocks[id]`, emitted only when that key is present (not
//    `undefined`) on the block object — mirrors the loader's per-file
//    `key in files` tolerance. NOTE: `buildLoadedTemplateFromFiles` always
//    populates `BlockDef.meta/state/events/data` with a synthesized default
//    when the source file was absent (never leaves them `undefined`) — so a
//    caller that wants THIS function to omit a file must thread "absent in
//    the source" through as an absent key on its own input, not as a
//    present-but-default value. That collapse happens inside the forward
//    map, not here; this function can only act on what it's given.
//
//  - `blocks/<id>/flow.json` <- `{ flows: input.blocks[id].flow }`, emitted
//    only when `flow` is present. The forward map unwraps `flow.json`'s
//    on-disk `{flows: FlowMap}` envelope into the bare `FlowMap` stored on
//    `BlockDef.flow` (`flowData.flows ?? flowData` — the loader ALSO
//    tolerates an unenveloped bare-FlowMap `flow.json`, but every template
//    on disk as of this writing uses the enveloped form, and
//    `project-api.ts`'s `buildBlockRows` uses the same envelope on the
//    write side). Since `BlockDef.flow` is always the unwrapped form
//    regardless of which on-disk shape produced it, re-wrapping is the only
//    well-defined inverse.
//
//  - `data/<name>.json` <- `input.appDataFiles[name]`, one file per key —
//    mirrors `LoadedApp.data`, keyed by datasource NAME (not path). This
//    function only knows the convention `data/<name>.json`; it does not
//    (and cannot, from this input alone) recover an arbitrary custom
//    `datasources.<name>.path` a manifest might declare.
//
// NOT invertible from this input alone — dropped by the FORWARD map itself,
// not by this function (see the round-trip test's explicit exclusion list
// for the concrete paths on the bakery-shop-new3 fixture): `app.spec.json`
// (never read by the loader), `blocks/<id>/ui.bindings.json` /
// `ui.template.json` (not one of the six block sub-files
// `assembleBlockFromFiles` reads), and `resources/theme-*.json` (the fixture's
// design-system.json is legacy-direct shape (colors.primary + radius, no axes),
// so `tryDeriveThemeTokensFromDesignSystem` yields tokens at higher priority;
// `buildLoadedTemplateFromFiles` never takes the themePath-reading branch,
// and when it does, theme content lands in `LoadedApp.tokens` (deliberately
// not consumed here), never in `designSystem` — so nothing byte-faithful is lost).

export interface SerializedBundle {
  files: Record<string, unknown>
}

export interface SerializeProjectToBundleBlockInput {
  ui?: unknown
  state?: unknown
  events?: unknown
  flow?: unknown
  data?: unknown
  meta?: unknown
}

export interface SerializeProjectToBundleInput {
  /** app.json source (LoadedApp.manifest shape). */
  manifest: Record<string, unknown>
  /** design-system.json source (LoadedApp.designSystem — NOT the derived LoadedApp.tokens; see header comment). */
  tokens: Record<string, unknown> | null
  /**
   * Fallback design-system.json source (LoadedApp.tokens — the
   * server-persisted design truth), used ONLY when `tokens` above is
   * absent. See header comment's "two-source rule" for why this is not a
   * lossy fallback for server projects specifically.
   */
  derivedTokens?: Record<string, unknown> | null
  blocks: Record<string, SerializeProjectToBundleBlockInput>
  /** data/<name>.json contents, keyed by datasource name (LoadedApp.data shape). */
  appDataFiles?: Record<string, unknown>
}

export function serializeProjectToBundle(input: SerializeProjectToBundleInput): SerializedBundle {
  const files: Record<string, unknown> = {}

  files['app.json'] = input.manifest

  if (input.tokens !== null && input.tokens !== undefined) {
    files['design-system.json'] = input.tokens
  } else if (input.derivedTokens !== null && input.derivedTokens !== undefined) {
    files['design-system.json'] = input.derivedTokens
  }

  for (const blockId of Object.keys(input.blocks).sort()) {
    const block = input.blocks[blockId]
    const base = `blocks/${blockId}`
    if (block.data !== undefined) files[`${base}/data.json`] = block.data
    if (block.events !== undefined) files[`${base}/events.json`] = block.events
    if (block.flow !== undefined) files[`${base}/flow.json`] = { flows: block.flow }
    if (block.meta !== undefined) files[`${base}/meta.json`] = block.meta
    if (block.state !== undefined) files[`${base}/state.json`] = block.state
    if (block.ui !== undefined) files[`${base}/ui.json`] = block.ui
  }

  if (input.appDataFiles) {
    for (const name of Object.keys(input.appDataFiles).sort()) {
      files[`data/${name}.json`] = input.appDataFiles[name]
    }
  }

  return { files }
}
